/* app/App.jsx — application root + routing foundation.
   ─────────────────────────────────────────────────────────────
   BrowserRouter lives at the outermost level so public routes (/reset-password,
   /invite) and useNavigate inside AuthGate both work without a router parent.

   Load order in main.jsx ensures window.AuthProvider, window.useAuth,
   window.SignInPage, window.ResetPasswordPage, window.InviteAcceptPage are all
   registered before App renders. */
import React, { useState, useEffect, useLayoutEffect, useRef, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { ToastProvider, useToast } from '../shared/feedback';
import { StoreProvider, useStore, applyBrandingGlobally } from '../shared/state';
import { AppLayout } from '../shared/layout';
import { RecordFormDrawer } from '../shared/forms';
import { ConfirmModal } from '../shared/overlays';
import PlatformRoutes from '../platform/index.jsx';
import OrganizationRoutes from '../organization/index.jsx';
import WorkspaceAdminRoutes from '../workspace-admin/index.jsx';
import { usePlatformData } from '../platform/platformStore';
import { RbacProvider, useRbac } from '../rbac/RbacContext';
import { RequireRole } from '../rbac/guards';
import { WorkspaceRuntimeProvider, useWorkspaceRuntime, useWorkspaceModules } from '../workspace/WorkspaceRuntimeContext';
import WorkspaceSwitcher from '../rbac/WorkspaceSwitcher';
import { roleFromSession, ROLES } from '../rbac/roles';
import { WS_MODULE_ROUTE_MAP } from '../shared/modules.js';

/* C-03: full route-id → backend module name map (covers all 43 previously ungated routes).
   Imported from shared/modules.js — single source of truth shared with layout/index.jsx. */
const WS_MODULE_MAP = WS_MODULE_ROUTE_MAP;

/* Route ↔ module-id maps */
const PATH_TO_MODULE = {
  '/': 'home', '/people': 'people', '/companies': 'companies', '/deals': 'deals',
  '/tasks': 'tasks', '/calendar': 'calendar', '/notes': 'notes', '/settings': 'settings',
  '/sales': 'sales', '/forecast': 'forecast', '/intelligence': 'intelligence', '/analytics': 'analytics',
  '/emails': 'emails', '/meetings': 'meetings', '/notifications': 'notifications',
  '/workflows': 'workflows', '/comm-analytics': 'comm-analytics',
  /* Phase 5 */
  '/copilot': 'copilot', '/llm-gateway': 'llm-gateway',
  '/automation': 'automation', '/approvals': 'approvals',
  '/custom-objects': 'custom-objects', '/forms': 'forms',
  '/integration': 'integration',
  '/reports': 'reports', '/exec-dashboard': 'exec-dashboard',
  '/goals': 'goals',
  '/commerce': 'commerce',
  '/predictive': 'predictive', '/monitoring': 'monitoring',
  /* Phase 7 — navigation integration */
  '/pipeline': 'pipeline', '/quotes': 'quotes', '/products': 'products',
  '/price-books': 'price-books', '/territory': 'territory',
  '/activities': 'activities', '/timeline': 'timeline',
  '/collaboration': 'collaboration', '/customer-360': 'customer-360',
  '/webhooks': 'webhooks', '/import-export': 'import-export',
  '/plugins': 'plugins', '/jobs': 'jobs',
  '/dashboards': 'dashboards', '/saved-searches': 'saved-searches',
  '/revenue-analytics': 'revenue-analytics',
  '/prompt-templates': 'prompt-templates', '/ai-sessions': 'ai-sessions',
  '/ml-scores': 'ml-scores', '/health-score': 'health-score',
  '/realtime': 'realtime',
};
const MODULE_TO_PATH = Object.fromEntries(Object.entries(PATH_TO_MODULE).map(([p, m]) => [m, p]));
const TITLES = {
  home: 'Home', people: 'People', companies: 'Companies', deals: 'Deals',
  tasks: 'Tasks', calendar: 'Calendar', notes: 'Notes', settings: 'Settings',
  sales: 'Sales Workspace', forecast: 'Revenue Forecast', intelligence: 'Pipeline Intelligence', analytics: 'Analytics',
  emails: 'Email', meetings: 'Meetings', notifications: 'Notifications',
  workflows: 'Workflows', 'comm-analytics': 'Communication Analytics',
  /* Phase 5 */
  copilot: 'AI Assistant', 'llm-gateway': 'AI Gateway',
  automation: 'Automation', approvals: 'Approvals',
  'custom-objects': 'Custom Objects', forms: 'Forms',
  integration: 'Integrations',
  reports: 'Reports', 'exec-dashboard': 'Executive Dashboard',
  goals: 'Goals & Territory',
  commerce: 'Commerce',
  predictive: 'Predictive Analytics', monitoring: 'Monitoring',
  /* Phase 7 — navigation integration */
  pipeline: 'Pipeline Management', quotes: 'Quotes', products: 'Products',
  'price-books': 'Price Books', territory: 'Territory',
  activities: 'Activities', timeline: 'Customer Timeline',
  collaboration: 'Collaboration', 'customer-360': 'Customer 360',
  webhooks: 'Webhooks', 'import-export': 'Import / Export',
  plugins: 'Plugins', jobs: 'Jobs',
  dashboards: 'Dashboards', 'saved-searches': 'Saved Searches',
  'revenue-analytics': 'Revenue Analytics',
  'prompt-templates': 'Prompt Templates', 'ai-sessions': 'AI Sessions',
  'ml-scores': 'ML Scores', 'health-score': 'Health Score',
  realtime: 'Live Feed',
};
const MODULE_LABELS = { ...TITLES };
const VIEW_LABELS = { kanban: 'Kanban View', calendar: 'Calendar View', table: null, list: null };

/* Pull a bridged component off window at render time */
const W = (name) => window[name] || (() => <div style={{ padding: 24, color: '#94A3B8' }}>Loading {name}…</div>);

/* Layout wrapper for all CRM routes — workspaceId comes from the URL (:workspaceId
   param), making workspace context session-independent and bookmark-safe. */
function CrmRuntimeWrapper() {
  const { workspaceId } = useParams();
  return (
    <WorkspaceRuntimeProvider workspaceId={workspaceId}>
      <Outlet />
    </WorkspaceRuntimeProvider>
  );
}

/* Shown during workspace runtime fetch or CRM data load — replaces the blank screen
   that previously occurred when WorkspaceRuntimeProvider had runtime=null. */
const WorkspaceSwitchingPage = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--nx-text-muted, #94A3B8)', fontSize: 14, gap: 10 }}>
    <span>Loading workspace…</span>
  </div>
);

/* Redirects legacy root path to the active workspace CRM or workspace picker. */
function CrmRootRedirect() {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  if (wsId) return <Navigate to={'/app/' + wsId} replace />;
  return <Navigate to="/workspaces" replace />;
}

/* Backward-compat: rewrites old flat CRM paths (/people, /deals, etc.) to the
   new URL-based scheme (/app/:workspaceId/people). */
function CrmPathRedirect({ sub }) {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  if (wsId) return <Navigate to={'/app/' + wsId + sub} replace />;
  return <Navigate to="/workspaces" replace />;
}

/* Thin wrappers that extract URL params and forward them as props to the
   bridged public pages (which can't use useParams directly). */
function ResetPasswordRoute() {
  const { token } = useParams();
  const Page = window.ResetPasswordPage;
  if (!Page) return null;
  return <Page token={token} />;
}

function InviteRoute() {
  const { token } = useParams();
  const Page = window.InviteAcceptPage;
  if (!Page) return null;
  return <Page token={token} />;
}

function PlatformInviteRoute() {
  const { token } = useParams();
  const Page = window.PlatformInviteAcceptPage;
  if (!Page) return null;
  return <Page token={token} />;
}

/* M-08: shows a toast before redirecting when a disabled-module route is navigated to
   directly (e.g. via bookmarked URL or programmatic push). Renders null while the effect
   queues the toast + navigation — avoids a silent redirect with no user feedback. */
function ModuleDisabledRedirect({ moduleName }) {
  const toast          = useToast();
  const navigate       = useNavigate();
  const { workspaceId } = useParams();
  useEffect(() => {
    toast({ tone: 'warning', icon: 'Layers', title: 'Module disabled', desc: moduleName + ' is not enabled for this workspace.' });
    navigate('/app/' + workspaceId, { replace: true });
  }, [toast, navigate, moduleName, workspaceId]);
  return null;
}

function WorkspaceShell() {
  const store = useStore();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId, can: canCrm } = useRbac();
  const { workspaceId } = useParams();
  const { loading: runtimeLoading } = useWorkspaceRuntime();
  const runtimeModules = useWorkspaceModules();
  const platform = usePlatformData();
  /* FOUNDATION-004B: resolve effective plan feature keys for the active org so the
     CRM sidebar can filter by org-level entitlements (no extra API call needed). */
  const activeOrg = platform.state.organizations.find(o => o.id === orgId);
  const planFeatureKeys = activeOrg?.planFeatureKeys || [];

  const wsBase = '/app/' + workspaceId;
  const wsSegment = location.pathname.startsWith(wsBase) ? location.pathname.slice(wsBase.length) || '/' : '/';
  /* CO-011c: detect /custom-objects/:apiName dynamic segments not in PATH_TO_MODULE */
  const _rawModule = PATH_TO_MODULE[wsSegment] || 'home';
  const _coApiName = (_rawModule === 'home' && wsSegment.startsWith('/custom-objects/'))
    ? (wsSegment.split('/')[2] || '')
    : '';
  const active = _coApiName ? 'co:' + _coApiName : _rawModule;
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [activeViewId, setActiveViewId] = useState(null);
  const [currentViewMode, setCurrentViewMode] = useState(null);
  const [viewNonce, setViewNonce] = useState(0);

  /* BUG-D: Re-apply workspace branding from the store cache when WorkspaceShell mounts.
     OrganizationLayout.useLayoutEffect cleanup calls clearBrandingTokens() on unmount,
     which removes all 13 CSS vars from :root. When the user then navigates back to the
     CRM, WorkspaceShell remounts but loadWorkspace does NOT re-run (wsId is unchanged),
     so store.branding is already populated — it just isn't applied to the DOM.
     Without this effect the CSS vars remain cleared and the page falls back to the
     stylesheet default (#125AB0 BLUE) until a full reload.
     useLayoutEffect (not useEffect) ensures re-application is synchronous before paint,
     eliminating any blue flash on navigation-back. Empty dependency array: intentional —
     we only need this on mount (the initial-load case is handled by loadWorkspace itself). */
  useLayoutEffect(() => {
    if (store.branding) applyBrandingGlobally(store.branding);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (m) => {
    setActiveViewId(null);
    if (m === 'platform') { navigate('/platform'); return; }
    if (m === 'workspaces') { navigate('/workspaces'); return; }
    if (m === 'org') { navigate('/organization/' + orgId); return; }
    /* CO-011c: per-object routes use 'co:<apiName>' as the module id */
    if (m.startsWith('co:')) { navigate('/app/' + workspaceId + '/custom-objects/' + m.slice(3)); return; }
    const sub = MODULE_TO_PATH[m] || '/';
    navigate('/app/' + workspaceId + (sub === '/' ? '' : sub));
  };

  /* Admin console access is now in ProfileMenu (topbar) and WorkspaceSwitcher,
     not in the CRM sidebar. No adminNav needed. */

  /* Global keyboard: ⌘K palette, ⌘1/2/3 nav */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdOpen(o => !o); return; }
      if ((e.metaKey || e.ctrlKey) && ['1', '2', '3'].includes(e.key)) {
        e.preventDefault();
        const map = { '1': 'deals', '2': 'people', '3': 'tasks' };
        const sub = MODULE_TO_PATH[map[e.key]] || '/';
        navigate('/app/' + workspaceId + (sub === '/' ? '' : sub));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  /* View-mode broadcast + saved-view re-key (parity with prototype) */
  useEffect(() => {
    const handler = (e) => setCurrentViewMode(e.detail);
    window.addEventListener('nx-view-change', handler);
    return () => window.removeEventListener('nx-view-change', handler);
  }, []);
  useEffect(() => { setCurrentViewMode(null); }, [active]);
  useEffect(() => {
    const handler = () => setViewNonce(n => n + 1);
    window.addEventListener('nx-saved-view-applied', handler);
    return () => window.removeEventListener('nx-saved-view-applied', handler);
  }, []);
  useEffect(() => { window.__nx_toast = toast; }, [toast]);

  /* CO-008: cross-module navigation from Custom Object drawers to native CRM entity drawers.
     Fires when RelatedRecordsSection dispatches 'nx-open-crm-record' with { type, id }. */
  useEffect(() => {
    const handler = (e) => {
      const { type, id } = e.detail || {};
      if (!type || !id) return;
      const collections = {
        people:    store.people    || [],
        companies: store.companies || [],
        deals:     store.deals     || [],
        tasks:     store.tasks     || [],
      };
      const entity = (collections[type] || []).find(r => r.id === id);
      if (entity) {
        store.openDrawer(entity);
      } else {
        const section = { people: 'people', companies: 'companies', deals: 'deals', tasks: 'tasks' }[type];
        if (section) navigate('/app/' + workspaceId + '/' + section);
      }
    };
    window.addEventListener('nx-open-crm-record', handler);
    return () => window.removeEventListener('nx-open-crm-record', handler);
  }, [store, workspaceId, navigate]);

  /* F-03: reset displayed columns for the entity being LEFT so that on return
     the entity shows __default__ columns, not a previously applied Saved View.
     Watches `active` (URL-derived current module) and fires for all navigation
     types: sidebar go(), keyboard shortcuts (⌘1/2/3 call navigate() directly),
     and direct URL changes. Targets the entity being LEFT so cfColumns is already
     reset before the entity view remounts on the return navigation. */
  const ENTITY_MODULES = new Set(['people', 'companies', 'deals', 'tasks']);
  const prevActiveRef = useRef(active);
  useEffect(() => {
    const prev = prevActiveRef.current;
    if (prev !== active) {
      if (ENTITY_MODULES.has(prev)) {
        store.resetEntityColumns(prev);
      }
      prevActiveRef.current = active;
    }
  }, [active, store]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Synchronous guards — evaluate BEFORE any protected content renders */
  /* NOTE: Super Admin is NOT blocked here. SA can enter a workspace CRM via
     "Open in CRM" (Directory.jsx / Detail.jsx) or WorkspaceSwitcher. The
     capability guard at line 239 (crm.use) is the authoritative access check.
     SA defaults to /platform on login (AuthGate) but may navigate to CRM
     legitimately — blocking by role alone breaks that intentional flow. */
  /* URL workspaceId is the authoritative source — no active workspace → workspace picker */
  if (!workspaceId) return <Navigate to="/workspaces" replace />;
  /* Show loading state during workspace switch or initial CRM data load */
  if (runtimeLoading || store.loading) return <WorkspaceSwitchingPage />;
  /* No CRM access capability for this workspace → workspace picker */
  if (!canCrm('crm.use')) return <Navigate to="/workspaces" replace />;

  const applySavedView = (view) => {
    if (!view) return;
    setActiveViewId(view.id);
    // LV-06: write filters + search + sort to sessionStorage (matches writeViewToSession in records.jsx)
    try {
      sessionStorage.setItem('nx-filters-' + view.module, JSON.stringify(view.filters || {}));
      sessionStorage.setItem('nx-filters-' + view.module + '-search', view.search || '');
      if (view.sort?.field) {
        sessionStorage.setItem('nx-sort-' + view.module, JSON.stringify(view.sort));
      } else {
        sessionStorage.removeItem('nx-sort-' + view.module);
      }
    } catch (_) {}
    // LV-06 FIX: use setColumnsFromView (INVARIANT-01 — must NOT call upsertDefault).
    // Previously used setCfColumns which triggered upsertDefault, corrupting __default__.
    if (view.columns !== undefined) {
      store.setColumnsFromView(view.module, view.columns);
    }
    window.dispatchEvent(new CustomEvent('nx-saved-view-applied', { detail: view }));
    const vsub = MODULE_TO_PATH[view.module] || '/';
    navigate('/app/' + workspaceId + (vsub === '/' ? '' : vsub));
    toast({ tone: 'success', icon: 'Star', title: 'View applied', desc: view.name });
  };

  const buildBreadcrumbs = () => {
    if (active === 'home') return [];
    /* CO-013f: co:* routes breadcrumb — show "Custom Objects" as a clickable ancestor.
       CO-04: links to the canonical WS Console manager, not the legacy CRM route. */
    if (_coApiName) {
      return [
        { label: 'Home', onClick: () => go('home') },
        { label: 'Custom Objects', onClick: () => navigate('/workspaces/' + workspaceId + '/customization/objects') },
      ];
    }
    const modLabel = MODULE_LABELS[active] || active;
    const vl = VIEW_LABELS[currentViewMode?.view];
    const sv = activeViewId ? store.savedViews.find(v => v.id === activeViewId) : null;
    const rec = store.drawer;
    const hasChildren = vl || sv || rec;
    const crumbs = [{ label: 'Home', onClick: () => go('home') }];
    crumbs.push({ label: modLabel, onClick: hasChildren ? () => go(active) : null });
    if (sv && !rec) crumbs.push({ label: sv.name, onClick: null });
    if (vl && !rec) crumbs.push({ label: vl, onClick: null });
    if (rec) {
      const recLabel = rec.name || rec.title || 'Record';
      const recModule = rec.role !== undefined ? 'People' : rec.domain !== undefined ? 'Companies' : 'Pipeline';
      if (recModule !== modLabel) crumbs.push({ label: recModule, onClick: null });
      crumbs.push({ label: recLabel, onClick: null });
    }
    return crumbs;
  };

  const renderModule = () => {
    const key = active + '-' + viewNonce;

    /* CO-011c / CO-05: custom object records page — co:* ids have no WS_MODULE_MAP entry so
       the generic gate below would never fire. Apply the CustomObjects gate explicitly here
       using the same WS_MODULE_MAP lookup and ModuleDisabledRedirect that all other routes use. */
    if (_coApiName) {
      /* CO-05: module gate — mirrors the existing gate at lines below for standard routes. */
      const _coBackendModule = WS_MODULE_MAP['custom-objects']; // 'CustomObjects'
      if (runtimeModules.length > 0 && !runtimeModules.includes(_coBackendModule)) {
        return <ModuleDisabledRedirect moduleName={_coBackendModule} />;
      }
      const C = W('CustomObjectRecordsPage');
      /* CO-04: back button navigates to canonical WS Console manager, not the legacy CRM route. */
      return <C key={key} wsId={workspaceId} apiName={_coApiName}
        onGoToManager={() => navigate('/workspaces/' + workspaceId + '/customization/objects')} />;
    }

    /* C-03: single upfront gate covers every route that has a module mapping.
       'home' and 'settings' are always reachable (no module gate).
       If runtimeModules is empty (loading) → permissive, all routes pass.
       If WS_MODULE_MAP has no entry for this route → no gate (unmapped routes always pass). */
    const backendModule = WS_MODULE_MAP[active];
    if (active !== 'home' && active !== 'settings' && backendModule) {
      if (runtimeModules.length > 0 && !runtimeModules.includes(backendModule)) {
        return <ModuleDisabledRedirect moduleName={backendModule} />;
      }
    }

    switch (active) {
      case 'home':             { const C = W('DashboardView');              return <C key={key} onNavigate={(v) => v === 'copilot' ? setCopilotOpen(true) : go(v)} onOpenRecord={store.openDrawer} />; }
      case 'people':           { const C = W('PeopleView');                 return <C key={key} />; }
      case 'companies':        { const C = W('CompaniesView');              return <C key={key} />; }
      case 'deals':            { const C = W('DealsView');                  return <C key={key} />; }
      case 'tasks':            { const C = W('TasksView');                  return <C key={key} />; }
      case 'calendar':         { const C = W('CalendarView');               return <C key={key} />; }
      case 'notes':            { const C = W('NotesView');                  return <C key={key} />; }
      case 'settings':         { const C = W('SettingsView');               return <C key={key} />; }
      case 'sales':            { const C = W('SalesWorkspaceView');         return <C key={key} />; }
      case 'forecast':         { const C = W('RevenueForecastView');        return <C key={key} />; }
      case 'intelligence':     { const C = W('PipelineIntelligenceView');   return <C key={key} />; }
      case 'analytics':        { const C = W('AnalyticsP6View');            return <C key={key} workspaceId={workspaceId} />; }
      case 'emails':           { const C = W('EmailInboxView');             return <C key={key} />; }
      case 'meetings':         { const C = W('MeetingsCalendarView');       return <C key={key} />; }
      case 'notifications':    { const C = W('NotificationCenter');         return <C key={key} onClose={() => go('home')} />; }
      case 'workflows':        { const C = W('WorkflowManagerView');        return <C key={key} />; }
      case 'comm-analytics':   { const C = W('CommunicationAnalyticsView'); return <C key={key} />; }
      case 'copilot':          { const C = W('CopilotChat');                return <C key={key} />; }
      case 'llm-gateway':      { const C = W('LlmGatewaySettings');         return <C key={key} />; }
      case 'automation':       { const C = W('AutomationManagerView');      return <C key={key} />; }
      case 'approvals':        { const C = W('ApprovalsView');              return <C key={key} />; }
      /* CO-NAV-001: /app/:wsId/custom-objects → neutral CRM landing (ACTIVE objects list).
         Records route /app/:wsId/custom-objects/:apiName is NOT affected (handled above by _coApiName branch).
         Admin "Manage Objects" shortcut inside the landing view links to the WS Console. */
      case 'custom-objects':   { const C = W('CustomObjectsLandingView');
        return <C key={key}
          onGoToManager={() => navigate('/workspaces/' + workspaceId + '/customization/objects')}
          onSelectObject={(apiName) => navigate('/app/' + workspaceId + '/custom-objects/' + apiName)} />; }
      case 'forms':            { const C = W('FormBuilderView');            return <C key={key} />; }
      case 'integration':      { const C = W('IntegrationHubView');         return <C key={key} />; }
      case 'reports':          { const C = W('ReportsListView');            return <C key={key} />; }
      case 'exec-dashboard':   { const C = W('ExecutiveDashboardView');     return <C key={key} />; }
      case 'goals':            { const C = W('GoalsTerritoryView');         return <C key={key} />; }
      case 'commerce':         { const C = W('CommerceHubView');            return <C key={key} />; }
      case 'predictive':       { const C = W('PredictiveAnalyticsView');    return <C key={key} />; }
      case 'monitoring':       { const C = W('MonitoringDashboardView');    return <C key={key} />; }
      case 'pipeline':         { const C = W('PipelineManagerView');        return <C key={key} />; }
      case 'quotes':           { const C = W('QuotesView');                 return <C key={key} />; }
      case 'products':         { const C = W('ProductCatalogView');         return <C key={key} />; }
      case 'price-books':      { const C = W('PriceBooksView');             return <C key={key} />; }
      case 'territory':        { const C = W('TerritoriesView');            return <C key={key} />; }
      case 'activities':       { const C = W('CustomerTimelineView');       return <C key={key} />; }
      case 'timeline':         { const C = W('CustomerTimelineView');       return <C key={key} />; }
      case 'collaboration':    { const C = W('CollaborationView');          return <C key={key} />; }
      case 'customer-360':     { const C = W('CommunicationAnalyticsView'); return <C key={key} />; }
      case 'webhooks':         { const C = W('WebhooksView');               return <C key={key} />; }
      case 'import-export':    { const C = W('ImportExportView');           return <C key={key} />; }
      case 'plugins':          { const C = W('PluginsView');                return <C key={key} />; }
      case 'jobs':             { const C = W('JobsView');                   return <C key={key} />; }
      case 'dashboards':       { const C = W('CustomDashboardsView');       return <C key={key} workspaceId={workspaceId} />; }
      case 'saved-searches':   { const C = W('EnterpriseSearchView');       return <C key={key} />; }
      case 'revenue-analytics':{ const C = W('ExecutiveDashboardView');     return <C key={key} />; }
      case 'prompt-templates': { const C = W('PromptTemplatesView');        return <C key={key} />; }
      case 'ai-sessions':      { const C = W('AiSessionsView');             return <C key={key} />; }
      case 'ml-scores':        { const C = W('PredictiveAnalyticsView');    return <C key={key} />; }
      case 'health-score':     { const C = W('HealthScoreDashboard');       return <C key={key} />; }
      case 'realtime':         { const C = W('RealtimeDashboardView');      return <C key={key} workspaceId={workspaceId} />; }
      default:                 { const C = W('DashboardView');              return <C key={key} onNavigate={go} onOpenRecord={store.openDrawer} />; }
    }
  };

  const RecordDrawer = W('RecordDrawer');
  const Copilot = W('Copilot');
  const CommandPalette = W('CommandPalette');

  return (
    <AppLayout
      active={active}
      onNavigate={go}
      workspaceSwitcher={<WorkspaceSwitcher data={platform} />}
      topbarTitle={_coApiName ? 'Custom Objects' : (TITLES[active] || 'URSA')}
      topbarBreadcrumbs={buildBreadcrumbs()}
      onCmd={() => setCmdOpen(true)}
      onCreate={(type) => store.openCreateForm(type)}
      savedViews={store.savedViews}
      activeViewId={activeViewId}
      onApplyView={applySavedView}
      onRemoveView={store.removeView}
      planFeatureKeys={planFeatureKeys}
      portals={<>
        <RecordDrawer />
        <RecordFormDrawer />
        <ConfirmModal />
        <Copilot open={copilotOpen} onClose={() => setCopilotOpen(false)} />
        <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} onNavigate={go} />
      </>}
    >
      {renderModule()}
    </AppLayout>
  );
}

/* Landing Resolver session key — module-scoped, owned by the navigation layer.
   Lifetime: page load → page unload. Survives React component remounts, StrictMode
   double-invoke, and ErrorBoundary resets. A new authenticated session produces a
   new key (user.id + signedInAt), making correctness self-verifying rather than
   dependent on explicit reset. */
let _landedSessionKey = null;

/* Auth gate — wraps window.AuthProvider so Inner can call window.useAuth().
   Handles loading spinner, sign-in redirect, and role-based post-login routing. */
function AuthGate({ children }) {
  const AuthProvider = window.AuthProvider;
  const Inner = () => {
    const { user, loading } = window.useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const SignInPage = window.SignInPage;

    /* AUTH-I-001: save intended destination while the user is unauthenticated.
       CRM routes are restored by Directory.openWorkspace() after workspace selection.
       Admin routes (platform/org/workspace-admin) are restored in the landing effect below. */
    useEffect(() => {
      if (user || loading) return;
      const path = location.pathname;
      const isCapturable = PATH_TO_MODULE[path]
        || path.startsWith('/platform/')
        || path.startsWith('/organization/')
        || path.startsWith('/workspaces/');
      if (path === '/' || !isCapturable) return;
      try { sessionStorage.setItem('nx-deep-link', path); } catch {}
    }, [user, loading, location.pathname]);

    /* Landing Resolver — fires ONCE per authenticated session.
       _landedSessionKey (module-level) gates the resolver so it never re-runs on
       user-driven navigation, React remounts, StrictMode double-invoke, or
       ErrorBoundary resets.

       Session identity: user.id + signedInAt. signedInAt = Date.now() on fresh
       login; preserved unchanged through switchOrg/switchWorkspace. A new login
       produces a new key → landing fires. Workspace/org switch preserves the key
       → landing skipped. Logout clears the key → next login gets fresh resolution.

       Session-establishment events (login, session restore, remember-me) always
       start at pathname '/' → resolve and navigate. User-driven navigation that
       lands at '/' (org switch, workspace switch, sidebar) → same key → return. */
    useEffect(() => {
      if (!user) { _landedSessionKey = null; return; }
      const sessionKey = user.id + '|' + (user.signedInAt || 0);
      if (_landedSessionKey === sessionKey) return;
      if (location.pathname !== '/') { _landedSessionKey = sessionKey; return; }
      _landedSessionKey = sessionKey;

      /* Restore admin-route deep links captured before login (platform/org/workspace-admin).
         CRM deep links (/deals, /people, etc.) are consumed by Directory.openWorkspace(). */
      try {
        const saved = sessionStorage.getItem('nx-deep-link');
        if (saved && !PATH_TO_MODULE[saved]) {
          sessionStorage.removeItem('nx-deep-link');
          navigate(saved, { replace: true });
          return;
        }
      } catch {}

      const normalized = roleFromSession(user.effectiveRole || user.platformRole);
      if (user.platformRole === 'SUPER_ADMIN') {
        navigate('/platform', { replace: true });
      } else if (normalized === ROLES.ORG_ADMIN) {
        const orgId = user.activeOrgId || user.contexts?.[0]?.orgId;
        if (orgId) navigate('/organization/' + orgId, { replace: true });
      } else if (user.activeWorkspaceId) {
        navigate('/app/' + user.activeWorkspaceId, { replace: true });
      } else if (user.contexts && user.contexts.length > 0) {
        navigate('/organization/' + user.contexts[0].orgId, { replace: true });
      } else if (user.activeOrgId) {
        navigate('/organization/' + user.activeOrgId, { replace: true });
      }
    }, [user, navigate, location.pathname]);

    if (loading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--nx-muted, #94A3B8)', fontSize: 14 }}>
          Restoring session…
        </div>
      );
    }
    if (!user) return <SignInPage />;
    return children;
  };
  return <AuthProvider><Inner /></AuthProvider>;
}

/* EB-001: catches unhandled render errors in child subtrees */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) { console.error('[ErrorBoundary]', err, info?.componentStack); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12, color: 'var(--nx-muted, #94A3B8)', fontSize: 14 }}>
          <span style={{ fontSize: 28 }}>⚠</span>
          <span>Something went wrong. Please refresh the page.</span>
          <button onClick={() => this.setState({ hasError: false })} style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid #334155', background: 'transparent', color: '#94A3B8', cursor: 'pointer' }}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* AUTH-N-001: consume nx-permission-denied events dispatched by client.js → show toast */
function PermissionDeniedListener() {
  const toast = useToast();
  useEffect(() => {
    const handler = () => toast({ tone: 'error', icon: 'ShieldX', title: 'Access denied', desc: "You don't have permission to perform this action." });
    globalThis.addEventListener('nx-permission-denied', handler);
    return () => globalThis.removeEventListener('nx-permission-denied', handler);
  }, [toast]);
  return null;
}

export default function App() {
  return (
    <ToastProvider>
      <PermissionDeniedListener />
      <BrowserRouter>
        <Routes>
          {/* Public routes — accessible without authentication */}
          <Route path="/reset-password/:token" element={<ResetPasswordRoute />} />
          <Route path="/invite/:token" element={<InviteRoute />} />
          <Route path="/platform-invite/:token" element={<PlatformInviteRoute />} />

          {/* All authenticated routes nested inside AuthGate */}
          <Route path="*" element={
            <ErrorBoundary>
              <AuthGate>
                <ErrorBoundary>
                  <StoreProvider>
                    <RbacProvider>
                  <Routes>
                    {/* Platform tier (Super Admin only) */}
                    <Route path="/platform/*" element={<RequireRole action="platform.view"><PlatformRoutes /></RequireRole>} />
                    {/* Organization tier (Org Admin+) */}
                    <Route path="/organization/:orgId/*" element={<RequireRole action="org.view"><OrganizationRoutes /></RequireRole>} />
                    {/* Workspace admin console */}
                    <Route path="/workspaces/*" element={<WorkspaceAdminRoutes />} />
                    {/* CRM workspace — URL-based: /app/:workspaceId/{module} */}
                    <Route path="/app/:workspaceId" element={<CrmRuntimeWrapper />}>
                      <Route index element={<WorkspaceShell />} />
                      {/* CO-011c: per-object records pages at /custom-objects/:apiName */}
                      <Route path="custom-objects/:apiName" element={<WorkspaceShell />} />
                      {Object.keys(PATH_TO_MODULE).filter(p => p !== '/').map(p => (
                        <Route key={p} path={p.slice(1)} element={<WorkspaceShell />} />
                      ))}
                    </Route>
                    {/* Legacy root — redirect to workspace-scoped URL */}
                    <Route path="/" element={<CrmRootRedirect />} />
                    {/* Backward-compat flat CRM paths */}
                    {Object.keys(PATH_TO_MODULE).filter(p => p !== '/').map(p => (
                      <Route key={'compat' + p} path={p} element={<CrmPathRedirect sub={p} />} />
                    ))}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                    </RbacProvider>
                  </StoreProvider>
                </ErrorBoundary>
              </AuthGate>
            </ErrorBoundary>
          } />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}
